// @ts-nocheck
import { useState, useRef } from "react";
import Editor from "@monaco-editor/react";
import { ShieldAlert, Zap, BrainCircuit, RefreshCw, Check, Play, SendHorizontal, StopCircle } from "lucide-react";
import { useApp } from "@/context/AppCtx";
import { consumeChatCompletionStream } from "@/lib/streamChat";
import { consumeAnthropicMessageStream } from "@/lib/streamAnthropic";
import { resolveChatCompletionUrl, resolveLlmApiUrl } from "@/lib/llmDevProxy";
import { anthropicMessagesUrl } from "@/lib/llmAnthropic";

const defaultRustTemplate = `use anchor_lang::prelude::*;

declare_id!("AssetHook1111111111111111111111111111111111");

#[program]
pub mod custom_asset_hook {
    use super::*;

    pub fn pre_transfer_check(ctx: Context<TransferHook>, amount: u64) -> Result<()> {
        let clock = Clock::get()?;
        
        // -----------------------------------------------------------------------
        // 🛡️ STANDARD PROTECTIONS
        // -----------------------------------------------------------------------
        
        // Example: Block 0 Snipe Protection
        // if clock.slot <= ctx.accounts.config.launch_slot + 5 {
        //     require!(amount <= 100_000, ErrorCode::SnipeAttemptBlocked);
        // }

        // Example: Cooldown Timer (Anti-HFT / Sandwiching)
        // let last_tx_time = ctx.accounts.user_state.last_tx_time;
        // require!(clock.unix_timestamp - last_tx_time >= 30, ErrorCode::CooldownActive);

        // -----------------------------------------------------------------------
        // 📈 MARKET MAKING & LIQUIDITY MANAGEMENT
        // -----------------------------------------------------------------------

        // Example: Dynamic Volatility Fees
        // If volume spikes, increase fee routing to LP to stabilize price
        // let current_volatility = ctx.accounts.oracle.get_volatility();
        // if current_volatility > HIGH_THRESH {
        //     apply_dynamic_fee(amount, 500 /* 5% */)?;
        // }

        // Example: Native SOL Asset Capture (Zero Contract Dumping)
        // Automatically route 1% of the transfer value directly to a Treasury PDA
        // without accumulating tokens that dump on the chart.
        // route_native_fee(&ctx, amount, 100 /* 1% */)?;

        // -----------------------------------------------------------------------
        // ⚠️ DANGEROUS / LIABILITY OPERATIONS
        // -----------------------------------------------------------------------
        // WARNING: Using these patterns violates 100% Transparency and may 
        // cause your token to be flagged as a Honeypot or Scam.

        // [DANGER] Whitelist-Only Selling (Honeypot Pattern)
        // require!(
        //     ctx.accounts.destination_asset.owner == ctx.accounts.admin.key(),
        //     ErrorCode::UnauthorizedTransfer
        // );

        // [DANGER] Hidden Developer Tax
        // Routes 10% of every transfer to a hardcoded wallet silently.
        // let hidden_tax = amount / 10;
        // secret_transfer(&ctx, hidden_tax)?;

        // [DANGER] Infinite Loop (Compute Unit Exhaustion)
        // DO NOT DO THIS. It will freeze the entire token liquidity pool.
        // loop {
        //    // This will consume all CUs and fail every transaction
        // }
        
        msg!("Hook triggered for amount: {}", amount);

        Ok(())
    }
}

#[derive(Accounts)]
pub struct TransferHook<'info> {
    pub source_asset: AccountInfo<'info>,
    pub destination_asset: AccountInfo<'info>,
}`;

const parentTokenTemplate = `// ============================================================================
// 📦 SOLANA TOKEN-2022 STANDARD IMPLEMENTATION (Core Engine)
// This is the parent execution environment where your token is deployed.
// Your custom asset hooks are invoked synchronously during every transfer.
// ============================================================================

use anchor_lang::prelude::*;
use anchor_spl::token_interface::{Mint, TokenAccount};

declare_id!("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA");

#[program]
pub mod token_2022_core {
    use super::*;

    /// Core transfer instruction invoked by all users, DEXs, and wallets
    pub fn transfer(ctx: Context<Transfer>, amount: u64) -> Result<()> {
        
        // 1. SPL Token Standard Validation
        require!(amount > 0, ErrorCode::InvalidAmount);
        require!(ctx.accounts.source.amount >= amount, ErrorCode::InsufficientFunds);
        
        // 2. Extract Token-2022 Transfer Hook Extension configurations
        let mint_info = &ctx.accounts.mint;
        if let Some(hook_program_id) = get_transfer_hook(mint_info) {
            
            // --------------------------------------------------------------
            // 🔄 CPI (Cross-Program Invocation) to YOUR Custom Hook
            // --------------------------------------------------------------
            msg!("Invoking custom asset hook: {}", hook_program_id);
            
            // This directly executes the code you wrote in the IDE
            custom_asset_hook::pre_transfer_check(
                CpiContext::new(
                    ctx.accounts.hook_program.to_account_info(),
                    custom_asset_hook::cpi::accounts::TransferHook {
                        source_asset: ctx.accounts.source.to_account_info(),
                        destination_asset: ctx.accounts.destination.to_account_info(),
                    }
                ),
                amount
            )?;
            // --------------------------------------------------------------
        }

        // 3. Complete the underlying ledger transfer
        let source = &mut ctx.accounts.source;
        let dest = &mut ctx.accounts.destination;
        
        source.amount = source.amount.checked_sub(amount).unwrap();
        dest.amount = dest.amount.checked_add(amount).unwrap();

        msg!("Transfer completed successfully");
        Ok(())
    }
}

// ============================================================================
// 🛡️ YOUR CUSTOM ASSET HOOK MODULE (Injected via CPI)
// ============================================================================

{{CUSTOM_CODE_HERE}}
`;

export function AlgoAdvancedIde() {
  const { model } = useApp() as any;
  const [customCode, setCustomCode] = useState(defaultRustTemplate);
  const [viewMode, setViewMode] = useState<"edit_hook" | "view_parent">("edit_hook");
  const [isCompiling, setIsCompiling] = useState(false);
  const [compileSuccess, setCompileSuccess] = useState(false);
  
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAiVerifying, setIsAiVerifying] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCompile = () => {
    setIsCompiling(true);
    setCompileSuccess(false);
    setAiFeedback(null);
    setTimeout(() => {
      setIsCompiling(false);
      setCompileSuccess(true);
    }, 1500);
  };

  const stopAi = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleAiGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;
    setIsGenerating(true);
    setCompileSuccess(false);
    setAiFeedback(null);
    
    abortControllerRef.current = new AbortController();
    const abort = abortControllerRef.current;
    
    let newCode = "";
    const systemPrompt = "You are an expert Solana Rust smart contract developer. You will be provided with the current code and a user prompt. Rewrite the code to implement the user's request. Output ONLY valid Rust code. Do not include markdown codeblocks or explanations.";

    try {
      const isAnthropic = model.baseUrl.includes("/__proxy/llm/anthropic");
      
      if (isAnthropic) {
        const url = resolveLlmApiUrl(anthropicMessagesUrl(model.baseUrl));
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": model.apiKey,
            "anthropic-version": "2023-06-01",
          },
          body: JSON.stringify({
            model: model.model,
            stream: true,
            max_tokens: 4096,
            system: systemPrompt,
            messages: [
              { role: "user", content: `CURRENT CODE:\n${customCode}\n\nUSER REQUEST:\n${prompt}` }
            ]
          }),
          signal: abort.signal,
        });
        
        setCustomCode("");
        await consumeAnthropicMessageStream(
          res.body,
          (chunk) => {
            newCode += chunk;
            setCustomCode(newCode);
          },
          { signal: abort.signal, idleMs: 60000 }
        );
      } else {
        const url = resolveChatCompletionUrl(model.baseUrl);
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${model.apiKey}`,
          },
          body: JSON.stringify({
            model: model.model,
            stream: true,
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: `CURRENT CODE:\n${customCode}\n\nUSER REQUEST:\n${prompt}` }
            ]
          }),
          signal: abort.signal,
        });

        setCustomCode("");
        await consumeChatCompletionStream(res.body, (chunk) => {
          newCode += chunk;
          setCustomCode(newCode);
        });
      }
    } catch (err: any) {
      if (err.name !== "AbortError") setAiFeedback(`Generate Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAiVerify = async () => {
    if (isAiVerifying) return;
    setIsAiVerifying(true);
    setAiFeedback("");
    
    abortControllerRef.current = new AbortController();
    const abort = abortControllerRef.current;
    
    let feedback = "";
    const systemPrompt = "You are an expert Solana Rust smart contract auditor. Analyze the following code for vulnerabilities, honeypot patterns, CU exhaustion, and reentrancy. Respond with a concise 1-2 sentence verdict. Do not use formatting.";

    try {
      const isAnthropic = model.baseUrl.includes("/__proxy/llm/anthropic");
      
      if (isAnthropic) {
        const url = resolveLlmApiUrl(anthropicMessagesUrl(model.baseUrl));
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": model.apiKey,
            "anthropic-version": "2023-06-01",
          },
          body: JSON.stringify({
            model: model.model,
            stream: true,
            max_tokens: 1024,
            system: systemPrompt,
            messages: [
              { role: "user", content: `CODE:\n${customCode}` }
            ]
          }),
          signal: abort.signal,
        });
        
        await consumeAnthropicMessageStream(
          res.body,
          (chunk) => {
            feedback += chunk;
            setAiFeedback(feedback);
          },
          { signal: abort.signal, idleMs: 60000 }
        );
      } else {
        const url = resolveChatCompletionUrl(model.baseUrl);
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${model.apiKey}`,
          },
          body: JSON.stringify({
            model: model.model,
            stream: true,
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: `CODE:\n${customCode}` }
            ]
          }),
          signal: abort.signal,
        });

        await consumeChatCompletionStream(res.body, (chunk) => {
          feedback += chunk;
          setAiFeedback(feedback);
        });
      }
    } catch (err: any) {
      if (err.name !== "AbortError") setAiFeedback(`Verify Error: ${err.message}`);
    } finally {
      setIsAiVerifying(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6 h-full relative z-10 animate-in fade-in duration-500 overflow-y-auto custom-scrollbar bg-[var(--color-bg-editor)] gap-4">
      {/* Security Disclosure Banner */}
      <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 flex gap-4 shrink-0 animate-in fade-in slide-in-from-top-4">
        <div className="size-8 rounded-full bg-rose-500/20 flex items-center justify-center shrink-0">
          <ShieldAlert className="size-4 text-rose-500" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-rose-500 mb-1">SECURITY & LIABILITY DISCLOSURE</h4>
          <p className="text-xs text-rose-500/80 leading-relaxed mb-2">
            Writing custom Rust hooks overrides our guaranteed "Zero Dumping" fee engine. If your code contains <strong>infinite loops (CU Exhaustion)</strong>, your token will permanently freeze. If it contains hidden routing, you violate transparency rules.
          </p>
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-400">
            Warning: Assets deployed with custom logic are permanently branded with an "UNAUDITED" badge to protect buyers. You assume full liability.
          </p>
        </div>
      </div>
      
      {/* Mode Tabs */}
      <div className="flex items-center gap-2 mb-2 shrink-0 animate-in fade-in slide-in-from-top-2">
        <button
          onClick={() => setViewMode("edit_hook")}
          className={`px-4 py-2 rounded-t-lg text-xs font-bold uppercase tracking-widest transition-colors ${viewMode === "edit_hook" ? "bg-[var(--color-border)] text-white" : "bg-black/20 text-white/40 hover:text-white/80"}`}
        >
          Edit Custom Hook
        </button>
        <button
          onClick={() => setViewMode("view_parent")}
          className={`px-4 py-2 rounded-t-lg text-xs font-bold uppercase tracking-widest transition-colors ${viewMode === "view_parent" ? "bg-[var(--color-border)] text-emerald-400" : "bg-black/20 text-emerald-400/40 hover:text-emerald-400/80"}`}
        >
          View Full Context (Read-Only)
        </button>
      </div>

      {viewMode === "view_parent" && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-4 flex gap-4 shrink-0 animate-in fade-in slide-in-from-top-4">
          <div className="size-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
            <BrainCircuit className="size-4 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-emerald-400 mb-1">SOLANA TOKEN-2022 CONTEXT EXPLAINER</h4>
            <p className="text-[12px] text-emerald-400/80 leading-relaxed">
              When you deploy your token, it runs inside the official <strong>Solana Token-2022 program</strong> (the "Parent Context"). You don't have to rewrite the complex math for token balances or approvals! Instead, the standard engine uses a <strong>Cross-Program Invocation (CPI)</strong> to call your custom `pre_transfer_check` hook right in the middle of every transaction.
              <br /><br />
              This Read-Only view shows exactly how your editable hook is injected into the standard transfer flow. Notice how the core ledger completes the transfer <i>only</i> if your hook succeeds.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex-1 min-h-[400px] flex flex-col rounded-b-xl rounded-tr-xl overflow-hidden border border-[var(--color-border)] relative shadow-[0_0_30px_rgba(0,0,0,0.5)] bg-[var(--color-bg-editor)]">
        {/* AI Prompt Box (Only in edit mode) */}
        {viewMode === "edit_hook" && (
          <div className="bg-black/40 border-b border-white/5 p-3 flex gap-2 shrink-0">
            <input
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
              placeholder="Instruct the AI to modify this code..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAiGenerate()}
          />
          {isGenerating || isAiVerifying ? (
            <button
              onClick={stopAi}
              className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 px-4 py-1.5 rounded-lg flex items-center justify-center transition-colors"
            >
              <StopCircle className="size-4" />
            </button>
          ) : (
            <button
              onClick={handleAiGenerate}
              disabled={!prompt.trim()}
              className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-1.5 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <SendHorizontal className="size-4" />
            </button>
          )}
        </div>
        )}

        <div className="flex-1 relative">
          <Editor
          height="100%"
          defaultLanguage="rust"
          theme="vs-dark"
          value={viewMode === "edit_hook" ? customCode : parentTokenTemplate.replace("{{CUSTOM_CODE_HERE}}", customCode)}
          onChange={(val) => {
            if (viewMode === "edit_hook") {
              setCustomCode(val || "");
              setCompileSuccess(false);
              setAiFeedback(null);
            }
          }}
          options={{
            readOnly: viewMode === "view_parent",
            minimap: { enabled: false },
            fontSize: 14,
            fontFamily: 'JetBrains Mono, monospace',
            padding: { top: 24, bottom: 24 },
            scrollBeyondLastLine: false,
            lineNumbersMinChars: 4,
          }}
        />
        </div>
      </div>
      
      {aiFeedback && (
        <div className="bg-black/60 border border-cyan-500/20 rounded-xl p-3 shrink-0 flex flex-col gap-1 max-h-48 overflow-y-auto custom-scrollbar animate-in fade-in slide-in-from-bottom-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-500/50">AI Audit Console</span>
          <span className="text-[12px] font-mono text-cyan-300 whitespace-pre-wrap">
            {aiFeedback}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between bg-black/40 border border-white/5 rounded-xl p-3 shrink-0">
        <div className="flex items-center gap-3">
          <button 
            onClick={handleCompile}
            disabled={isCompiling}
            className="bg-purple-500 hover:bg-purple-400 text-black px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {isCompiling ? (
              <span className="flex items-center gap-1"><Zap className="size-4 animate-pulse" /> Compiling...</span>
            ) : compileSuccess ? (
              <span className="flex items-center gap-1"><Check className="size-4" /> Compiled</span>
            ) : (
              <span className="flex items-center gap-1"><Play className="size-4" /> Build</span>
            )}
          </button>

          <button 
            onClick={handleAiVerify}
            disabled={isAiVerifying || !compileSuccess}
            className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all disabled:opacity-50 group"
          >
            {isAiVerifying ? (
              <span className="flex items-center gap-1.5"><RefreshCw className="size-4 animate-spin" /> Analyzing...</span>
            ) : (
              <span className="flex items-center gap-1.5"><BrainCircuit className="size-4 group-hover:scale-110 transition-transform" /> AI Audit</span>
            )}
          </button>
        </div>
        
        <button 
          disabled={!compileSuccess}
          className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs uppercase tracking-widest rounded-lg transition-all disabled:opacity-50"
        >
          Save custom hooks
        </button>
      </div>
    </div>
  );
}
